import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import BaseModal from '../utils/BaseModal/BaseModal';
import Input from '../../utils/inputs/Input/Input';
import SelectAlt from '../../utils/SelectAlt/SelectAlt';
import { ButtonWrapper, CancelButton, Form, SubmitButton } from './ReportMessageModal.styles';
import { createReview } from '../../../store/slices/chatter/asyncThunks';
import { selectChatterActionsPending } from '../../../store/slices/chatter/slice';
import { notifyError, notifyInfo } from '../../../utils/notify';

export const REPORT_REASON = {
  NOT_USING_AI_CHAT_AFTER_CONVERT: 'NOT_USING_AI_CHAT_AFTER_CONVERT',
  NOT_USING_AI_CHAT_AFTER_LOCKED_PVV: 'NOT_USING_AI_CHAT_AFTER_LOCKED_PVV',
  NOT_FOLLOWING_SEXTING_SET_ORDER: 'NOT_FOLLOWING_SEXTING_SET_ORDER',
  LADDERING_FROM_MULTIPLE_SEXTING_SETS: 'LADDERING_FROM_MULTIPLE_SEXTING_SETS',
  BURNING_SEXTING_SETS: 'BURNING_SEXTING_SETS',
  BREAKING_LADDER_PRICE: 'BREAKING_LADDER_PRICE',
  NOT_HANDLING_HIGH_PRICE_OBJECTIONS: 'NOT_HANDLING_HIGH_PRICE_OBJECTIONS',
  DRAINING_SAME_GUY_TWICE: 'DRAINING_SAME_GUY_TWICE',
  OTHER: 'OTHER',
};

const REASON_OPTIONS = [
  { value: REPORT_REASON.NOT_USING_AI_CHAT_AFTER_CONVERT, label: 'Not using AI chat after they convert' },
  {
    value: REPORT_REASON.NOT_USING_AI_CHAT_AFTER_LOCKED_PVV,
    label: 'Not using AI chat after they send a locked PVV and fan objects',
  },
  { value: REPORT_REASON.NOT_FOLLOWING_SEXTING_SET_ORDER, label: 'Not following sexting set order' },
  { value: REPORT_REASON.LADDERING_FROM_MULTIPLE_SEXTING_SETS, label: 'Laddering from multiple sexting sets' },
  { value: REPORT_REASON.BURNING_SEXTING_SETS, label: 'Burning sexting sets' },
  { value: REPORT_REASON.BREAKING_LADDER_PRICE, label: 'Breaking the ladder price without fan asking for it' },
  {
    value: REPORT_REASON.NOT_HANDLING_HIGH_PRICE_OBJECTIONS,
    label: 'Not handling a high price objections by lowering price',
  },
  { value: REPORT_REASON.DRAINING_SAME_GUY_TWICE, label: 'Trying to drain the same guy twice a day after a big spend' },
  { value: REPORT_REASON.OTHER, label: 'Other' },
];

const reportMessageFormSchema = yup
  .object({
    reason: yup
      .object({ value: yup.string().required(), label: yup.string() })
      .nullable()
      .required('Reason is required'),
    description: yup
      .string()
      .trim()
      .when('reason', {
        is: reason => reason?.value === REPORT_REASON.OTHER,
        then: schema => schema.required('Description is required when reason is "Other"'),
        otherwise: schema => schema.notRequired(),
      }),
  })
  .required();

const ReportMessageModal = ({ modalRef, interactionId, onSuccess }) => {
  const dispatch = useDispatch();
  const { createReview: createReviewPending } = useSelector(selectChatterActionsPending);

  const {
    handleSubmit,
    control,
    register,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reason: null,
      description: '',
    },
    delayError: 300,
    resolver: yupResolver(reportMessageFormSchema),
  });

  const reasonWatch = watch('reason');
  const isOther = reasonWatch?.value === REPORT_REASON.OTHER;

  const onSubmit = data => {
    if (!interactionId) {
      notifyError('Missing interaction to report.');
      return;
    }

    const trimmedDescription = data.description?.trim();

    dispatch(
      createReview({
        reason: data.reason.value,
        interactionId,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      }),
    )
      .unwrap()
      .then(() => {
        modalRef.current.hide();
        onSuccess?.();
        notifyInfo('Message reported successfully!');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <BaseModal
      title="Report message"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        reset();
      }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="reason"
          control={control}
          render={({ field }) => (
            <SelectAlt
              label="Reason"
              placeholder="Select reason"
              options={REASON_OPTIONS}
              selectedOption={field.value}
              handleChange={field.onChange}
              error={errors.reason?.message}
            />
          )}
        />
        <Input
          label={`Description${isOther ? '' : ' (optional)'}`}
          placeholder="Describe the issue"
          type="textarea"
          textAreaHeight={100}
          {...register('description')}
          error={errors.description?.message}
        />
        <ButtonWrapper>
          <CancelButton
            title="Cancel"
            onClick={() => {
              reset();
              modalRef.current.hide();
            }}
            type="button"
            disabled={createReviewPending}
          />
          <SubmitButton type="submit" title="Submit" isLoading={createReviewPending} />
        </ButtonWrapper>
      </Form>
    </BaseModal>
  );
};

export default ReportMessageModal;
